"use client";
import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { iconComponent } from "@components/ui/IconComponent";
import cookieConfig from "@data/config/cookie-consent.json";
import {
  trackCookieConsentEvent,
  trackCookieConsentButton,
  trackCookieToggle,
} from "@utils/ga4Tracking";
import {
  loadGoogleTags,
  announceGA4Ready,
  readStoredConsent,
  type ConsentSettings,
} from "@utils/ga4Consent";
import "@assets/styles/cookie-consent.scss";

// === CONFIG ===
const config = cookieConfig;

// === ICONS ===
const {
  cookie: Cookie,
  chevronDown: ChevronDown,
  chevronUp: ChevronUp,
  check: Check,
  analytics: Analytics,
  marketing: Marketing,
  x: X,
  settings: Settings,
} = iconComponent;

// === TYPES ===
interface CookieConsentContextType {
  isVisible: boolean;
  hasConsented: boolean;
  consentSettings: ConsentSettings | null;
  isMiniMode: boolean;
  updateConsent: (settings: ConsentSettings) => void;
  toggleMiniMode: () => void;
  openFullConsent: () => void;
}

// === CONTEXT ===
const CookieConsentContext = createContext<CookieConsentContextType>({
  isVisible: true,
  hasConsented: false,
  consentSettings: null,
  isMiniMode: false,
  updateConsent: () => {},
  toggleMiniMode: () => {},
  openFullConsent: () => {},
});

// === CUSTOM HOOK ===
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      "useCookieConsent must be used within CookieConsentProvider"
    );
  }
  return context;
};

// === PROVIDER COMPONENT ===
export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const [consentSettings, setConsentSettings] =
    useState<ConsentSettings | null>(null);
  const [isMiniMode, setIsMiniMode] = useState(false);

  // Helper function to load scripts when consent is found on page reload
  const loadScriptsOnConsent = useCallback((consentSettings: ConsentSettings) => {
    const loaded = loadGoogleTags(
      consentSettings,
      config.cookieConsent.tracking
    );

    if (loaded) {
      // Tell the app GA is ready
      setTimeout(announceGA4Ready, 100);
    }
  }, []);

  // Initialisierung - nur auf Client-Seite
  useEffect(() => {
    // Verhindere Hydration-Mismatch durch Client-only Rendering
    if (typeof window === "undefined") return;

    try {
      const savedConsent = readStoredConsent();
      if (savedConsent) {
        setConsentSettings(savedConsent);
        setHasConsented(true);
        setIsVisible(false);
        setIsMiniMode(true); // Nach dem Laden in Mini-Modus

        // Always hand the stored choice to the loader — it decides what to
        // inject. Gating this on `analytics` alone meant a marketing-only
        // consent never re-loaded the Ads tag after a reload.
        loadScriptsOnConsent(savedConsent);
      } else {
        // console.log("🍪 No consent found - showing cookie banner");
        const timer = setTimeout(
          () => setIsVisible(true),
          config.cookieConsent.timing.showDelay
        );
        return () => clearTimeout(timer);
      }
    } catch (error) {
      console.error("Error loading cookie consent:", error);
      setTimeout(
        () => setIsVisible(true),
        config.cookieConsent.timing.showDelay
      );
    }
  }, [loadScriptsOnConsent]);

  // Update Consent Handler
  const updateConsent = useCallback((settings: ConsentSettings) => {
    setConsentSettings(settings);
    setHasConsented(true);
    const timer = setTimeout(() => {
      setIsVisible(false);
      // Kleiner Delay für das Mini-Icon, damit die Animation sauber läuft
      setTimeout(() => {
        setIsMiniMode(true);
      }, 100);
    }, config.cookieConsent.timing.animationDuration);
    return () => clearTimeout(timer);
  }, []);

  // Toggle Mini Mode
  const toggleMiniMode = useCallback(() => {
    setIsMiniMode((prev) => !prev);
  }, []);

  // Full Consent öffnen
  const openFullConsent = useCallback(() => {
    setIsMiniMode(false);
    setIsVisible(true);
  }, []);

  const contextValue = useMemo(
    () => ({
      isVisible,
      hasConsented,
      consentSettings,
      isMiniMode,
      updateConsent,
      toggleMiniMode,
      openFullConsent,
    }),
    [
      isVisible,
      hasConsented,
      consentSettings,
      isMiniMode,
      updateConsent,
      toggleMiniMode,
      openFullConsent,
    ]
  );

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
    </CookieConsentContext.Provider>
  );
};

// === MAIN COOKIE CONSENT COMPONENT ===
const CookieConsent: React.FC<{ isAbisPage?: boolean }> = ({
  isAbisPage = false,
}) => {
  const { isVisible, updateConsent } = useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [exitDirection, setExitDirection] = useState<"down" | "right">("down");
  const [cookies, setCookies] = useState<ConsentSettings>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  const cfg = config.cookieConsent;

  // Load saved consent settings from localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const savedConsent = localStorage.getItem("cookieConsent");
        if (savedConsent) {
          const parsedConsent = JSON.parse(savedConsent);
          console.log(
            "📂 Loading saved consent from localStorage:",
            parsedConsent
          );
          setCookies(parsedConsent);
        }
      } catch (error) {
        console.error("❌ Error loading saved consent:", error);
      }
    }
  }, []);

  // Update cookies state when details panel opens
  useEffect(() => {
    if (showDetails && typeof window !== "undefined") {
      try {
        const savedConsent = localStorage.getItem("cookieConsent");
        if (savedConsent) {
          const parsedConsent = JSON.parse(savedConsent);
          console.log(
            "🔄 Updating cookies state when opening details:",
            parsedConsent
          );
          setCookies(parsedConsent);
        }
      } catch (error) {
        console.error("❌ Error updating cookies state:", error);
      }
    }
  }, [showDetails]);

  // Script Loader
  const loadScripts = useCallback(
    (consentSettings: ConsentSettings, trackingAction?: string) => {
      const loaded = loadGoogleTags(consentSettings, cfg.tracking);

      if (!loaded) {
        // Nothing consented — consent stays denied and no tag is injected.
        // Signal readiness immediately so saveConsent never waits on a timeout.
        announceGA4Ready();
        return;
      }

      // Track the consent event now that GA4 is configured
      if (trackingAction) {
        const page = isAbisPage ? "abis_page" : "home_page";
        trackCookieConsentEvent(
          trackingAction,
          {
            analytics: consentSettings.analytics,
            marketing: consentSettings.marketing,
          },
          page
        );
      }

      // Tell the app GA is ready
      setTimeout(announceGA4Ready, 100);
    },
    [cfg.tracking, isAbisPage]
  );

  // Save Consent Handler
  const saveConsent = useCallback(
    (consentSettings: ConsentSettings, trackingAction?: string) => {
      try {
        console.log("💾 Saving consent to localStorage:", consentSettings);
        setIsExiting(true);
        setExitDirection("right"); // Animation nach rechts (zum Mini-Icon)
        localStorage.setItem("cookieConsent", JSON.stringify(consentSettings));
        console.log("✅ Consent saved to localStorage");

        // Load scripts and wait for GA4 to be ready before updating consent
        loadScripts(consentSettings, trackingAction);

        // If analytics is accepted, wait for GA4 to be ready before updating consent
        if (consentSettings.analytics) {
          console.log("🍪 Analytics accepted - waiting for GA4 to be ready");

          // Listen for GA4 ready event
          const handleGA4Ready = () => {
            console.log("🎯 GA4 is ready - updating consent state");
            setTimeout(() => {
              updateConsent(consentSettings);
              setShowDetails(false);
            }, cfg.timing.animationDuration);
            window.removeEventListener("ga4Ready", handleGA4Ready);
          };

          // Add event listener for GA4 ready
          window.addEventListener("ga4Ready", handleGA4Ready);

          // Fallback timeout in case GA4 doesn't fire the ready event
          setTimeout(() => {
            console.log("⏰ GA4 ready timeout - updating consent state anyway");
            updateConsent(consentSettings);
            setShowDetails(false);
            window.removeEventListener("ga4Ready", handleGA4Ready);
          }, 2000); // 2 second fallback
        } else {
          // No analytics, update consent immediately
          console.log("🍪 No analytics - updating consent state immediately");
          setTimeout(() => {
            updateConsent(consentSettings);
            setShowDetails(false);
          }, cfg.timing.animationDuration);
        }
      } catch (error) {
        console.error("❌ Error saving cookie consent:", error);
      }
    },
    [loadScripts, updateConsent, cfg.timing.animationDuration]
  );

  // Action Handlers
  const handleAcceptAll = () => {
    console.log("🍪 User accepting all cookies - will track after GA4 loads");

    const allAccepted: ConsentSettings = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    saveConsent(allAccepted, "cookie_accept_all");
  };

  const handleAcceptSelected = () => {
    console.log(
      "🍪 User accepting selected cookies - will track after GA4 loads if analytics enabled"
    );

    saveConsent(cookies, "cookie_accept_selected");
  };

  const handleRejectAll = () => {
    console.log("🍪 User rejecting all cookies - no GA4 loading needed");

    const onlyNecessary: ConsentSettings = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    saveConsent(onlyNecessary, "cookie_reject_all");
  };

  const toggleCookie = (category: keyof ConsentSettings) => {
    if (category !== "necessary") {
      console.log(`🔄 Toggling ${category} cookie`);

      // Track toggle event before state change
      const newValue = !cookies[category];
      const page = isAbisPage ? "abis_page" : "home_page";
      trackCookieToggle(category as "analytics" | "marketing", newValue, page);

      setCookies((prev) => {
        const newState = {
          ...prev,
          [category]: !prev[category],
        };
        console.log(`✅ ${category} cookie toggled to:`, newState[category]);
        return newState;
      });
    } else {
      console.log("⚠️ Cannot toggle necessary cookies");
    }
  };

  // Professionell aus dem DOM entfernen - nicht nur ausgeblendet
  if (!isVisible) return null;

  return (
    <>
      {/* === MAIN CONTAINER === */}
      <div className="relative w-full">
        {/* === DETAILS PANEL - Öffnet nach oben === */}
        <div
          className={`
            cookie-card cookie-card--details
            absolute left-0 right-0
            transition-all duration-300 ease-in-out bottom-16
            ${
              showDetails
                ? "opacity-100 translate-y-0 visible"
                : "opacity-0 translate-y-4 invisible pointer-events-none"
            }
          `}
        >
          <div className="p-5 md:p-6 max-h-[60vh] md:max-h-[70vh] overflow-y-auto overscroll-contain">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                Cookie-Einstellungen anpassen
              </h3>
              <button
                onClick={() => {
                  const page = isAbisPage ? "abis_page" : "home_page";
                  trackCookieConsentButton("details_toggle", page);
                  setShowDetails(false);
                }}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Einstellungen schließen"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Cookie Categories */}
            <div className="space-y-4 mb-6">
              {/* Necessary */}
              <div className="cookie-category cookie-category--disabled">
                <label className="cookie-option cookie-option--disabled">
                  <input
                    type="checkbox"
                    checked={cookies.necessary}
                    disabled
                    className="cookie-checkbox"
                    aria-label={cfg.categories.necessary.title}
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="cookie-option__title">
                        {cfg.categories.necessary.title}
                      </span>
                      <span className="cookie-badge cookie-badge--required">
                        {cfg.categories.necessary.badge}
                      </span>
                    </div>
                    <p className="cookie-option__description">
                      {cfg.categories.necessary.description}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {cfg.categories.necessary.examples.map((example, idx) => (
                        <span key={idx} className="cookie-tag">
                          {example}
                        </span>
                      ))}
                    </div>
                  </div>
                </label>
              </div>

              {/* Analytics */}
              <div
                className={`cookie-category ${
                  !cookies.analytics ? "opacity-50" : ""
                }`}
              >
                <label
                  className="cookie-option cursor-pointer"
                  onClick={() => toggleCookie("analytics")}
                >
                  <input
                    type="checkbox"
                    checked={cookies.analytics}
                    onChange={() => toggleCookie("analytics")}
                    className="cookie-checkbox"
                    aria-label={cfg.categories.analytics.title}
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Analytics
                          className="h-4 w-4 text-primary"
                          aria-hidden="true"
                        />
                        <span className="cookie-option__title">
                          {cfg.categories.analytics.title}
                        </span>
                        {/* Subtile Indikation für eingeschränkte Features */}
                        <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full border border-blue-200">
                          Basis-Tracking
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {cfg.categories.analytics.provider}
                      </span>
                    </div>
                    <p className="cookie-option__description">
                      {cfg.categories.analytics.description}
                    </p>
                    <ul className="mt-2 space-y-1">
                      {cfg.categories.analytics.benefits.map((benefit, idx) => (
                        <li key={idx} className="cookie-benefit">
                          <Check
                            className="h-3 w-3 text-green-500"
                            aria-hidden="true"
                          />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </label>
              </div>

              {/* Marketing */}
              <div
                className={`cookie-category ${
                  !cookies.marketing ? "opacity-50" : ""
                }`}
              >
                <label
                  className="cookie-option cursor-pointer"
                  onClick={() => toggleCookie("marketing")}
                >
                  <input
                    type="checkbox"
                    checked={cookies.marketing}
                    onChange={() => toggleCookie("marketing")}
                    className="cookie-checkbox"
                    aria-label={cfg.categories.marketing.title}
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Marketing
                          className="h-4 w-4 text-orange-600"
                          aria-hidden="true"
                        />
                        <span className="cookie-option__title">
                          {cfg.categories.marketing.title}
                        </span>
                        {/* Subtile Indikation für deaktivierte Features */}
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full border border-gray-200">
                          Bald verfügbar
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {cfg.categories.marketing.provider}
                      </span>
                    </div>
                    <p className="cookie-option__description">
                      {cfg.categories.marketing.description}
                    </p>
                    <ul className="mt-2 space-y-1">
                      {cfg.categories.marketing.benefits.map((benefit, idx) => (
                        <li key={idx} className="cookie-benefit">
                          <Check
                            className="h-3 w-3 text-green-500"
                            aria-hidden="true"
                          />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="cookie-footer">
              <nav className="flex gap-3 text-xs" aria-label="Rechtliche Links">
                <a href={cfg.footer.privacyLink.url} className="cookie-link">
                  {cfg.footer.privacyLink.text}
                </a>
                <span className="text-gray-400" aria-hidden="true">
                  •
                </span>
                <a href={cfg.footer.imprintLink.url} className="cookie-link">
                  {cfg.footer.imprintLink.text}
                </a>
              </nav>
              <button onClick={handleAcceptSelected} className="btn btn-hero">
                {cfg.footer.saveButton}
              </button>
            </div>
          </div>
        </div>

        {/* === MAIN COOKIE BAR === */}
        <div
          className={`
            cookie-card
            transition-all duration-300 ease-in-out rounded-2xl
            ${
              isExiting
                ? exitDirection === "right"
                  ? "opacity-0 translate-x-full scale-95" // Animation nach rechts
                  : "opacity-0 translate-y-4 scale-95" // Animation nach unten
                : "opacity-100 translate-x-0 translate-y-0 scale-100"
            }
          `}
          role="dialog"
          aria-label="Cookie Einstellungen"
          aria-describedby="cookie-description"
        >
          <div className="flex items-center justify-between gap-2 md:gap-4 md:px-4 px-3 py-4">
            {/* Icon & Text - Mobile optimiert */}
            <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
              {/* Cookie Icon - versteckt auf sehr kleinen Screens */}
              <Cookie
                className="h-6 w-6 md:h-10 md:w-10 text-primary flex-shrink-0 hidden sm:block"
                aria-hidden="true"
              />

              {/* Text - Gekürzte Mobile Version */}
              <p
                id="cookie-description"
                className="cookie-description-text text-gray-600 m-0 truncate md:whitespace-normal"
              >
                <span className="md:hidden">{cfg.main.descriptionShort}</span>
                <span className="hidden md:inline">{cfg.main.description}</span>
              </p>
            </div>

            {/* Action Buttons - Kompakt auf Mobile */}
            <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
              {/* Settings Button - Nur Icon auf Mobile */}
              <button
                onClick={() => {
                  const page = isAbisPage ? "abis_page" : "home_page";
                  trackCookieConsentButton("details_toggle", page);
                  setShowDetails(!showDetails);
                }}
                className="btn cookie-btn--ghost p-2 md:px-2 md:py-2"
                aria-expanded={showDetails ? "true" : "false"}
                aria-controls="cookie-details"
                aria-label={
                  showDetails ? cfg.main.lessButton : cfg.main.moreButton
                }
              >
                {/* Mobile: Nur Settings Icon */}
                <span className="md:hidden">
                  <Settings className="h-4 w-4" />
                </span>

                {/* Desktop: Text + Chevron */}
                <span className="hidden md:flex items-center gap-1">
                  {showDetails ? cfg.main.lessButton : cfg.main.moreButton}
                  {showDetails ? (
                    <ChevronUp className="h-3 w-3" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="h-3 w-3" aria-hidden="true" />
                  )}
                </span>
              </button>

              {/* Reject Button - Kurzer Text auf Mobile */}
              <button
                onClick={handleRejectAll}
                className="btn cookie-btn--ghost text-xs px-2 md:px-2"
              >
                <span className="md:hidden">Nein</span>
                <span className="hidden md:inline">
                  {cfg.main.rejectButtonShort}
                </span>
              </button>

              {/* Accept Button - Kurzer Text auf Mobile */}
              {!showDetails && (
                <button onClick={handleAcceptAll} className="btn btn-hero">
                  <span className="md:hidden">OK</span>
                  <span className="hidden md:inline">
                    {cfg.main.acceptAllButton}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// === MINI COOKIE ICON COMPONENT ===
const MiniCookieIcon: React.FC<{ isAbisPage: boolean }> = ({ isAbisPage }) => {
  const { openFullConsent, isVisible: isConsentVisible } = useCookieConsent();
  const [isVisible, setIsVisible] = useState(false);
  const [isInHeroOrFooter, setIsInHeroOrFooter] = useState(false);
  const [hideTimer, setHideTimer] = useState<NodeJS.Timeout | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Mobile Detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Hero & Footer Area Detection (für alle Geräte)
  useEffect(() => {
    let hideTimer: NodeJS.Timeout | null = null;

    // Scroll-basierte Detection (funktioniert immer)
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight;

      // Hero: Top 50% der Seite
      const isInHero = scrollTop < documentHeight * 0.5;
      // Footer: Letzten 20% der Seite
      const isInFooter = scrollTop > documentHeight * 0.8;

      const inHeroOrFooter = isInHero || isInFooter;

      setIsInHeroOrFooter(inHeroOrFooter);

      // Timer-Management direkt im Scroll-Handler
      if (inHeroOrFooter) {
        // Clear existing timer
        if (hideTimer) clearTimeout(hideTimer);

        // Set new timer to hide after 3 seconds
        hideTimer = setTimeout(() => {
          setIsVisible(false);
        }, 3000);
      } else {
        // Clear timer and show immediately
        if (hideTimer) {
          clearTimeout(hideTimer);
          hideTimer = null;
        }
        setIsVisible(true);
      }
    };

    // Initial check
    handleScroll();

    // Add scroll listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []); // Empty dependency array - no infinite loop!

  // Einblend-Animation nach dem Mounten (nur wenn nicht in Hero/Footer)
  useEffect(() => {
    if (!isInHeroOrFooter) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [isInHeroOrFooter]);

  // Wenn Cookie Consent sichtbar ist, Icon ausblenden
  const shouldShowIcon = isVisible && !isConsentVisible;

  return (
    <div className="fixed bottom-5 left-5 z-[1000]">
      <button
        onClick={() => {
          const page = isAbisPage ? "abis_page" : "home_page";
          trackCookieConsentButton("details_toggle", page);
          openFullConsent();
        }}
        className={`
          bg-white/80 backdrop-blur-md shadow-header-primary 
          transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:shadow-xl
          rounded-2xl
          ${
            shouldShowIcon
              ? isInHeroOrFooter
                ? "opacity-100 translate-x-0 scale-50 p-2 hover:scale-[0.55]"
                : "opacity-100 translate-x-0 scale-100 p-4 hover:scale-110"
              : "opacity-0 translate-x-8 scale-95 pointer-events-none p-4"
          }
        `}
        aria-label="Cookie-Einstellungen öffnen"
        title="Cookie-Einstellungen anpassen"
      >
        <Cookie
          className={`text-primary transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
            isInHeroOrFooter ? "h-8 w-8" : "h-8 w-8"
          }`}
        />
      </button>
    </div>
  );
};

// === FIXED BOTTOM CONTAINER ===
// Cookie Consent für ALLE Seiten mit ABIS-spezifischem Styling
export const FixedBottomContainer: React.FC<{
  children?: React.ReactNode;
}> = () => {
  const { isVisible, hasConsented, isMiniMode } = useCookieConsent();
  const [isClient, setIsClient] = useState(false);

  // Verhindere Hydration-Mismatch durch Client-only Rendering
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Erkenne automatisch, ob wir auf der ABIS Seite sind
  const abisPaths = [
    "/agentic-ai-tool/power-bi-mcp-server",
    "/services/power-bi-mcp-server",
    "/services/agentic-bi-tool",
    "/agentic-bi-tool",
  ];
  const isAbisPage =
    typeof window !== "undefined" &&
    abisPaths.includes(window.location.pathname);

  // Rendere nichts auf Server-Seite
  if (!isClient) return null;

  // Wenn Cookie Consent sichtbar ist, Container rendern
  if (isVisible) {
    return (
      <div className="cookie-wrapper">
        <div className="cookie-container">
          <div className="flex items-end gap-5 justify-between">
            {/* Cookie Consent */}
            <div className="order-1 flex-1">
              <CookieConsent isAbisPage={isAbisPage} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Wenn in Mini-Modus, das kleine Icon links unten anzeigen
  if (hasConsented && isMiniMode) {
    return <MiniCookieIcon isAbisPage={isAbisPage} />;
  }

  // Weder Consent sichtbar noch bereits consentiert - nichts rendern
  return null;
};

export default CookieConsent;
